import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { adminApi, type MountPoint, type MountPointSortField } from '../api/admin';
import { DataListFrame, SortHeader, stickyTh, useListControls } from '../components/data-list';

// マウントポイント = 論理名↔物理パスの割当マスター（論理名↔DIRマスター）。
// 【大前提】name(論理名=マウントポイント) が以後 backend/DB/frontend/worker の全参照の起点。
// 物理パス(path)を書いてよい唯一の場所。実在チェックはここではしない（worker 連携層で後段）。
// 削除は破壊的（参照中の設定が全部無効になる）ため、GitHub 風に名前の手入力で確認させる。

const SORT_OPTIONS: { value: MountPointSortField; label: string }[] = [
  { value: 'id', label: 'ID' },
  { value: 'name', label: '論理名' },
  { value: 'path', label: 'パス' },
  { value: 'createdAt', label: '作成日' },
];

export function MountPointsPage() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['mount-points'] });

  const controls = useListControls<MountPointSortField>('konecco.list.mount-points', {
    q: '',
    sortBy: 'id',
    sortDir: 'asc',
    page: 1,
    pageSize: 20,
  });
  // 有効/無効 = ドロップダウン絞り込み。'' は指定なし。
  const [enabledFilter, setEnabledFilter] = useState<'' | 'true' | 'false'>('');
  const mountPointsQuery = useQuery({
    queryKey: [
      'mount-points',
      'search',
      controls.q,
      enabledFilter,
      controls.sortBy,
      controls.sortDir,
      controls.page,
      controls.pageSize,
    ],
    queryFn: () =>
      adminApi.searchMountPoints({
        q: controls.q,
        enabled: enabledFilter === '' ? undefined : enabledFilter === 'true',
        sortBy: controls.sortBy,
        sortDir: controls.sortDir,
        page: controls.page,
        pageSize: controls.pageSize,
      }),
    placeholderData: keepPreviousData,
  });
  const items = mountPointsQuery.data?.items ?? [];
  const total = mountPointsQuery.data?.total ?? 0;

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MountPoint | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MountPoint | null>(null);

  const createMountPoint = useMutation({
    mutationFn: adminApi.createMountPoint,
    onSuccess: () => {
      setCreateOpen(false);
      invalidate();
    },
  });
  const updateMountPoint = useMutation({
    mutationFn: (vars: {
      id: number;
      input: { name?: string; path?: string; description?: string | null; enabled?: boolean };
    }) => adminApi.updateMountPoint(vars.id, vars.input),
    onSuccess: () => {
      setEditTarget(null);
      invalidate();
    },
  });
  const deleteMountPoint = useMutation({
    mutationFn: adminApi.deleteMountPoint,
    onSuccess: () => {
      setDeleteTarget(null);
      invalidate();
    },
  });
  // 一覧から直接「有効/無効」を切り替える（履歴のため削除せず無効化できる）。
  const toggleEnabled = (m: MountPoint) =>
    updateMountPoint.mutate({ id: m.id, input: { enabled: !m.enabled } });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p className="k-muted" style={{ margin: 0, fontSize: 13 }}>
        論理名↔ディレクトリ（物理パス）の割当マスター。<strong>物理パスを書いてよいのはここだけ</strong>です。
        以後の設定はこの「論理名（マウントポイント）」を参照し、実行時にパスへ解決します。
        パスの実在チェックは行いません（デバイス側で後段確認）。
      </p>
      {mountPointsQuery.error && (
        <p className="k-error">取得に失敗しました: {(mountPointsQuery.error as Error).message}</p>
      )}
      {deleteMountPoint.error && (
        <p className="k-error">削除に失敗しました: {(deleteMountPoint.error as Error).message}</p>
      )}
      <DataListFrame
        title="マウントポイント"
        total={total}
        loading={mountPointsQuery.isLoading}
        controls={controls}
        sortOptions={SORT_OPTIONS}
        searchPlaceholder="あいまい検索…（論理名・パス・説明）"
        filters={
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="k-muted" style={{ fontSize: 12 }}>
              状態
            </span>
            <select
              value={enabledFilter}
              onChange={(e) => {
                setEnabledFilter(e.target.value as '' | 'true' | 'false');
                controls.setPage(1);
              }}
              style={{ padding: '7px 10px', fontSize: 13 }}
            >
              <option value="">すべて</option>
              <option value="true">有効</option>
              <option value="false">無効</option>
            </select>
          </label>
        }
        rightActions={
          <button type="button" onClick={() => setCreateOpen(true)}>
            ＋ 新規作成
          </button>
        }
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortHeader field="id" controls={controls}>id</SortHeader>
              <SortHeader field="name" controls={controls}>論理名（マウントポイント）</SortHeader>
              <SortHeader field="path" controls={controls}>パス（実体DIR）</SortHeader>
              <th style={stickyTh}>状態</th>
              <th style={stickyTh}>操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={td}>{m.id}</td>
                <td style={td}>
                  <strong style={mono}>{m.name}</strong>
                  {m.description && (
                    <div className="k-muted" style={{ fontSize: 12, marginTop: 2 }}>
                      {m.description}
                    </div>
                  )}
                </td>
                <td style={{ ...td, maxWidth: 360, color: '#475569', wordBreak: 'break-all' }}>
                  <span style={mono}>{m.path}</span>
                </td>
                <td style={td}>
                  <button
                    className="btn-ghost"
                    type="button"
                    disabled={updateMountPoint.isPending}
                    title="クリックで有効/無効を切替"
                    onClick={() => toggleEnabled(m)}
                    style={{ color: m.enabled ? '#16a34a' : '#94a3b8', fontWeight: 700 }}
                  >
                    {m.enabled ? '● 有効' : '○ 無効'}
                  </button>
                </td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-ghost" type="button" onClick={() => setEditTarget(m)}>
                      変更
                    </button>
                    <button
                      className="btn-danger"
                      type="button"
                      onClick={() => setDeleteTarget(m)}
                    >
                      削除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataListFrame>

      {createOpen && (
        <MountPointFormDialog
          mode="create"
          submitting={createMountPoint.isPending}
          error={createMountPoint.error as Error | null}
          onClose={() => setCreateOpen(false)}
          onSubmit={(v) => createMountPoint.mutate(v)}
        />
      )}
      {editTarget && (
        <MountPointFormDialog
          mode="edit"
          initial={editTarget}
          submitting={updateMountPoint.isPending}
          error={updateMountPoint.error as Error | null}
          onClose={() => setEditTarget(null)}
          onSubmit={(v) => updateMountPoint.mutate({ id: editTarget.id, input: v })}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmDialog
          target={deleteTarget}
          submitting={deleteMountPoint.isPending}
          error={deleteMountPoint.error as Error | null}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => deleteMountPoint.mutate(deleteTarget.id)}
        />
      )}
    </div>
  );
}

interface MountPointFormValues {
  name: string;
  path: string;
  description: string | null;
  enabled: boolean;
}

function MountPointFormDialog({
  mode,
  initial,
  onSubmit,
  onClose,
  submitting,
  error,
}: {
  mode: 'create' | 'edit';
  initial?: MountPoint;
  onSubmit: (v: MountPointFormValues) => void;
  onClose: () => void;
  submitting: boolean;
  error: Error | null;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [path, setPath] = useState(initial?.path ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const labelStyle = { fontSize: 13, fontWeight: 600, color: '#334155' } as const;
  const fieldWrap = { display: 'flex', flexDirection: 'column', gap: 4 } as const;
  const valid = name.trim() !== '' && path.trim() !== '';

  return (
    <Modal
      title={mode === 'create' ? '新しいマウントポイントを登録' : `マウントポイントを変更 — #${initial?.id}`}
      onClose={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid) return;
          onSubmit({
            name: name.trim(),
            path: path.trim(),
            description: description.trim() || null,
            enabled,
          });
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div style={fieldWrap}>
          <label style={labelStyle}>論理名（マウントポイント・以後の参照の起点）</label>
          <input
            placeholder="例: konecco / konecco-docs / worker-state"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            style={{ fontFamily: 'monospace' }}
          />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>パス（マウント先の実体ディレクトリ）</label>
          <input
            placeholder="例: /home/uemura/Projects/9987_konecco"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            required
            style={{ fontFamily: 'monospace' }}
          />
          <span className="k-muted" style={{ fontSize: 12 }}>
            実在チェックはしません。存在しないパスも登録可（デバイス側で後段確認）。
          </span>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>説明（任意）</label>
          <textarea
            placeholder="このマウントポイントの用途メモ"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          有効にする（無効にしても履歴のため残ります）
        </label>
        {error && <p className="k-error">{error.message}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-ghost" type="button" onClick={onClose}>
            キャンセル
          </button>
          <button type="submit" disabled={submitting || !valid}>
            {mode === 'create' ? '登録' : '変更を保存'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// 破壊的削除。GitHub 風に、論理名を正確に手入力させてからでないと削除できない。
function DeleteConfirmDialog({
  target,
  onConfirm,
  onClose,
  submitting,
  error,
}: {
  target: MountPoint;
  onConfirm: () => void;
  onClose: () => void;
  submitting: boolean;
  error: Error | null;
}) {
  const [typed, setTyped] = useState('');
  const matched = typed === target.name;

  return (
    <Modal title={`マウントポイントを削除 — #${target.id}`} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            padding: '12px 14px',
            color: '#991b1b',
            fontSize: 13.5,
            lineHeight: 1.7,
          }}
        >
          <strong style={{ display: 'block', fontSize: 14, marginBottom: 4 }}>
            ⚠ この操作は取り消せません
          </strong>
          論理名{' '}
          <code style={{ fontWeight: 700 }}>{target.name}</code>{' '}
          を削除すると、<strong>このマウントポイントを参照している設定がすべて無効になります。</strong>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
            確認のため、論理名{' '}
            <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>
              {target.name}
            </code>{' '}
            を入力してください
          </label>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={target.name}
            autoFocus
            style={{ fontFamily: 'monospace' }}
          />
        </div>
        {error && <p className="k-error">{error.message}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-ghost" type="button" onClick={onClose}>
            キャンセル
          </button>
          <button
            className="btn-danger"
            type="button"
            disabled={!matched || submitting}
            onClick={onConfirm}
          >
            {submitting ? '削除中…' : '完全に削除する'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Modal({
  title,
  onClose,
  children,
  width = 480,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '7vh 16px',
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="k-card"
        style={{ width: '100%', maxWidth: width, maxHeight: '85vh', overflow: 'auto' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 14,
          }}
        >
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button className="btn-ghost" type="button" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const td = { padding: '8px 12px', fontSize: 14, verticalAlign: 'top' } as const;
const mono = { fontFamily: 'monospace' } as const;
